import { useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';

import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CodeIcon from '@mui/icons-material/Code';
import HandymanIcon from '@mui/icons-material/Handyman';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import GavelIcon from '@mui/icons-material/Gavel';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

import BreakdownBlock, { formatNumber, formatDate } from '../breakdownBlock/breakdownBlock';

// Icon per sale type — the labels come fixed from the API (saleTypeLabel), which
// mirrors the "Tipo Movimento" combo of the Gerente's sale screen
const TYPE_ICONS = {
  'Venda': <PointOfSaleIcon />,
  'Manutenção': <BuildCircleIcon />,
  'Hora Técnica': <AccessTimeIcon />,
  'Desenvolvimento': <CodeIcon />,
  'Serviço': <HandymanIcon />,
  'Assistência Técnica': <SupportAgentIcon />,
  'Serasa': <GavelIcon />,
  'Permuta': <SwapHorizIcon />,
  'Acessórios': <DevicesOtherIcon />,
  'Orçamento': <RequestQuoteIcon />,
};

// The types that carry the volume keep the same color across periods, so the
// chart reads the same way month after month
const TYPE_COLORS = {
  'Venda': { light: '#2a78d6', dark: '#3987e5' },              // azul
  'Assistência Técnica': { light: '#1baf7a', dark: '#199e70' }, // verde
  'Acessórios': { light: '#e08b2a', dark: '#e89b3f' },          // laranja
};

const ORIGIN_ICONS = {
  'Cliente Sirrus': <StorefrontIcon />,
  'Novo Cliente': <PersonAddAltIcon />,
  'Não informada': <HelpOutlineIcon />,
};

// The same three colours the growth chart splits its Vendas bar with, so a green
// block means "novo cliente" on both. Grey is the absence of an origin, not a
// third one — and it is the only bucket allowed to be dull.
const ORIGIN_COLORS = {
  'Cliente Sirrus': { light: '#2a78d6', dark: '#3987e5' },  // azul
  'Novo Cliente': { light: '#1baf7a', dark: '#199e70' },    // verde
  'Não informada': { light: '#8a8a82', dark: '#a6a69c' },   // cinza
};

// Turns an API group ({ description, items }) into a category for the block:
// group -> client -> the sales themselves. The observation typed on the sale is
// what leads the row, the same way the cancellations tab leads with the reason
// as it was written in the Gerente.
const toCategories = (groups, iconFor, colorFor) =>
  (groups || []).map((group) => {
    const byClient = new Map();

    for (const sale of group.items || []) {
      const name = sale.clientName || 'Sem cliente';
      if (!byClient.has(name)) byClient.set(name, { name, value: 0, entries: [] });
      const client = byClient.get(name);
      client.value = +(client.value + (sale.netAmount || 0)).toFixed(2);

      const saleLabel = sale.saleNumber ? `Venda ${sale.saleNumber}` : 'Venda sem número';
      client.entries.push({
        id: sale.id,
        // With an observation it leads the line; without one the sale identifies
        // itself, so the list never shows a row of empty text
        primary: sale.observation || saleLabel,
        secondary: [
          sale.observation ? saleLabel : null,
          sale.confirmedDate ? formatDate(sale.confirmedDate) : null,
          sale.seller,
          sale.maintenanceValue ? `manutenção ${formatNumber(sale.maintenanceValue)}` : null,
        ].filter(Boolean).join(' · '),
        value: sale.netAmount || 0,
      });
    }

    for (const client of byClient.values()) {
      client.entries.sort((a, b) => b.value - a.value);
    }

    return {
      id: group.description,
      colorKey: group.description,
      name: `${group.description} (${group.count})`,
      icon: iconFor(group.description),
      color: colorFor ? colorFor(group.description) : undefined,
      value: group.netAmount || 0,
      children: Array.from(byClient.values()),
    };
  });

export default function SalesTypeBreakdown({ sales }) {
  const theme = useTheme();
  const mode = theme.palette.mode === 'dark' ? 'dark' : 'light';

  const typeCategories = useMemo(
    () => toCategories(
      sales?.byType?.groups,
      (name) => TYPE_ICONS[name] || <MoreHorizIcon />,
      (name) => TYPE_COLORS[name]?.[mode],
    ),
    [sales, mode]
  );

  const originCategories = useMemo(
    () => toCategories(
      sales?.byOrigin?.groups,
      (name) => ORIGIN_ICONS[name] || <MoreHorizIcon />,
      (name) => ORIGIN_COLORS[name]?.[mode],
    ),
    [sales, mode]
  );

  const totalNetAmount = sales?.totalNetAmount || 0;
  const totalCount = sales?.byType?.totalCount || 0;
  const totalMaintenance = sales?.byType?.totalMaintenance || 0;

  return (
    <Box>
      {/* Period summary */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center', mb: 2 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">Vendas</Typography>
          <Typography sx={{ fontSize: 18, fontWeight: 700 }}>{totalCount}</Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">Valor líquido</Typography>
          <Typography sx={{ fontSize: 18, fontWeight: 700 }}>{formatNumber(totalNetAmount)}</Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">Manutenção gerada</Typography>
          <Typography sx={{ fontSize: 18, fontWeight: 700 }}>{formatNumber(totalMaintenance)}</Typography>
        </Box>
      </Box>

      <BreakdownBlock
        title="Vendas por tipo"
        categories={typeCategories}
        mode={mode}
        emptyLabel="Sem vendas no período"
        keepZeroValues
      />

      <Box sx={{ height: 24 }} />

      <BreakdownBlock
        title="Vendas por origem da venda"
        categories={originCategories}
        mode={mode}
        emptyLabel="Sem vendas no período"
        keepZeroValues
      />
    </Box>
  );
}
